import {
  forwardRef,
  ElementType,
  ReactNode,
  useMemo,
  useContext,
  memo,
} from 'react';
import { useWatch } from 'react-hook-form';

import { useLanguage } from 'assets/languages';
import { CAMPAIGN_DTO_ENUMS } from 'api/repositories/campaign/campaign.constants';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';

interface PhotoModelFieldProps {
  as?: ElementType;
  label: ReactNode;
  count: number;
  border?: boolean;
  decrement: () => void;
  increment: () => void;
}

const PhotoModelField = forwardRef<HTMLLIElement, PhotoModelFieldProps>(
  ({
    as: Component = 'li',
    border = false,
    label,
    count,
    decrement,
    increment,
  }: PhotoModelFieldProps, ref) => {
    const language = useLanguage();
    const languageTexts = {
      ...language.pages.campaigns,
    };
    const { modal, campaignFormMethods, guidelineRuleFormMethods } =
      useContext(CampaignFormContext);

    const outputTypeFl = useWatch({
      control: campaignFormMethods.control,
      name: 'outputTypeFl',
    });
    const [photoModelFull, photoModelHalf, photoModelCloseup] = useWatch({
      control: guidelineRuleFormMethods.control,
      name: [
        GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_FULL,
        GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_HALF,
        GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_CLOSEUP,
      ],
    });

    const totalPhotoModelCount = useMemo(() => {
      return (
        photoModelFull?.length +
        photoModelHalf?.length +
        photoModelCloseup?.length
      );
    }, [photoModelFull, photoModelHalf, photoModelCloseup]);

    const onClickDecrement = () => {
      if (totalPhotoModelCount > 0) {
        decrement();
      }
    };

    const onClickIncrement = () => {
      const limit =
        outputTypeFl === CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO ? 10 : 3;

      if (totalPhotoModelCount < limit) {
        // 5개 이상 추가할 때 안내 모달
        if (totalPhotoModelCount === 4) {
          modal.showModal({
            hideCloseButton: true,
            isCompact: true,
            text: languageTexts.campaign_form_photo_model_recommended_modal_text,
          });
        }

        increment();
      } else {
        // 사진 최대 개수 안내 모달
        modal.showModal({
          title: languageTexts.campaign_form_photo_model_limit_modal_title,
          text: languageTexts.campaign_form_photo_model_limit_modal_text(limit),
        });
      }
    };

    return (
      <Component
        className={`flex justify-between items-center gap-3 pb-[23px] ${
          border ? 'border-b border-[#E8E8E8]' : ''
        }`}
      >
        {/* Label */}
        <div className="text-[15px] font-bold whitespace-pre-wrap tracking-tighter">
          {label}
        </div>

        {/* Counter */}
        <div className="flex items-center">
          {/* Decrement */}
          <button
            type="button"
            className="border border-gray-300 rounded-full w-6 h-6 focus:ring-gray-100 focus:ring-2 focus:outline-none"
            onClick={onClickDecrement}
          >
            <svg
              className="mx-auto"
              width="6"
              height="2"
              viewBox="0 0 6 2"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="6" height="1" fill="black" />
            </svg>
          </button>

          {/* Count */}
          <p className="w-7 border-0 text-black text-center block outline-none">
            {count}
          </p>

          {/* Increment */}
          <button
            type="button"
            className="border border-gray-300 rounded-full w-6 h-6 focus:ring-gray-100 focus:ring-2 focus:outline-none"
            onClick={onClickIncrement}
          >
            <svg
              className="mx-auto"
              width="6"
              height="6"
              viewBox="0 0 6 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="2.5" width="6" height="1" fill="black" />
              <rect
                x="3.5"
                width="6"
                height="1"
                transform="rotate(90 3.5 0)"
                fill="black"
              />
            </svg>
          </button>
        </div>
      </Component>
    );
  },
);

export default memo(PhotoModelField);
